import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MusicalNoteIcon } from '@heroicons/react/24/solid';

import { useModal } from 'hooks/useModal';

import NotificationOption from './NotificationOption';
import NotificationStatus from './NotificationStatus';
import Modal from 'components/Modal';
import Button from 'components/Button';

import { timeDifference } from 'utils/helpers';
import { getErrorCode } from 'utils/getErrorCode';
import { errorCodes } from 'utils/errorCodes';

const { STORAGE_LIMIT_EXCEEDED } = errorCodes;

const NotificationRow = ({ invite, blockUser, replyNotification, unblockUser }) => {
  const { t, i18n } = useTranslation();

  const { onOpen: openFullStorageModal } = useModal('FullStorageModal');

  const [isOpen, setIsOpen] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [show, setShow] = useState();
  const [isDenied, setIsDenied] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [selected, setSelected] = useState({});
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSelect = (data) => {
    setSelected(data);
    setShow(true);
  };

  const toggleBlocked = () => setIsBlocked(!isBlocked);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleAccept = async () => {
    const response = await replyNotification(invite.id, 'accepted');
    if (getErrorCode(response) === STORAGE_LIMIT_EXCEEDED) {
      openFullStorageModal();
      setError(t('notification.full_storage'));
    } else {
      setIsAccepted(true);
    }
  };

  const handleDeny = async () => {
    await replyNotification(invite.id, 'denied');
    setIsDenied(true);
  };

  return (
    <>
      <Modal show={show} setShow={setShow}>
        <div>
          <div className='px-10 text-center gap-y-3'>
            <h3>{isBlocked ? t('global.allow') : t('global.block')} user</h3>
            <span className='text-base text-neutral-silver-200'>
              Are you sure you want to {isBlocked ? t('global.allow') : t('global.block')} this user?
            </span>
            <div className='text-neutral-silver-200'>
              <span className='text-white'>{selected.full_name}</span> @{selected.username}
            </div>
          </div>
          <div className='font-archivo font-semibold flex mt-8 lg:gap-4 gap-3'>
            <Button text={'Cancel'} onClick={() => setShow(!isOpen)} style={'tertiary'} />
            <Button
              text={isBlocked ? t('global.allow') : t('global.block')}
              onClick={() => {
                isBlocked
                  ? unblockUser(selected.user_id, () => setShow(!isOpen), toggleBlocked)
                  : blockUser(selected.user_id, () => setShow(!isOpen), toggleBlocked);
              }}
              style={'primary'}
            />
          </div>
        </div>
      </Modal>
      <div className='row'>
        <div className='flex items-center justify-between '>
          <div className='flex gap-x-4 items-center w-[95%]'>
            <div className='bg-neutral-black rounded-lg flex justify-center items-center p-2.5 md:p-3'>
              <MusicalNoteIcon className='md:h-7 md:w-7 h-5 w-5 text-white' />
            </div>
            <div className='flex flex-col flex-1 min-w-0'>
              <div
                className={`md:text-lg text-base max-w-[360px] ${isExpanded ? 'whitespace-normal' : 'truncate'}`}
                onClick={toggleExpand}
                style={{ cursor: 'pointer' }}
              >
                {invite.content[i18n.language === 'en' ? 'en' : 'es'].title}
              </div>
              <div className='md:text-base text-sm text-neutral-silver-200'>
                {invite.content[i18n.language === 'en' ? 'en' : 'es'].body}
              </div>
            </div>
          </div>
          <div className='flex gap-x-3 items-center'>
            <div className='md:flex hidden'>
              <span className='text-neutral-silver-200 text-sm min-w-[80px]'>{timeDifference(invite.date)}</span>
            </div>
            <NotificationStatus status={invite.status} isOpen={isOpen} setIsOpen={setIsOpen} expired={invite.expired} />
          </div>
        </div>
        {isOpen && (
          <NotificationOption
            isOpen={isOpen}
            onDeny={handleDeny}
            onAccept={handleAccept}
            isDenied={isDenied}
            isAccepted={isAccepted}
            blockUser={() => handleSelect(invite)}
            unblockUser={() => handleSelect(invite)}
            isBlocked={isBlocked}
            error={error}
          />
        )}
      </div>
    </>
  );
};

export default NotificationRow;
